import { useEffect, useMemo, useState } from "react";
import {
  bundleLookupCourseId,
  eligibleBundlesFor,
  membershipResolveItems,
  type MembershipResolution,
  type UserApplicableBundle,
} from "@baluu/ui/lib/checkout/payment-options";
import type { BasketItem } from "~/widgets/stores/basket-store";
import { apiOrigin } from "~/widgets/lib/basket-validate";

const NO_BUNDLES: UserApplicableBundle[] = [];

type Params = {
  apiUrl: string;
  businessId: string;
  items: BasketItem[];
  /** The SIGNED-IN account's email, never the buyer form field: bundles and
   *  membership credits may only be spent from the authenticated customer's
   *  account. Null when signed out — nothing is looked up. */
  accountEmail: string | null;
};

/**
 * The non-card ways to pay this basket — the signed-in customer's bundles
 * that can cover it, and what their membership covers — as the widget's
 * full-page checkout looks them up (apps/widget/.../useCheckoutPaymentOptions)
 * and on the same shared rules (`@baluu/ui/lib/checkout/payment-options`).
 *
 * Unlike the widget, each lookup is keyed to what decides its answer (the
 * booked courses and sessions), not to the item objects: the overlay's
 * validate rewrites every item on each answer, which would otherwise re-ask
 * both endpoints after every availability check.
 */
export function useBasketPaymentOptions({
  apiUrl,
  businessId,
  items,
  accountEmail,
}: Params): {
  eligibleBundles: UserApplicableBundle[];
  membershipResolution: MembershipResolution | null;
} {
  const bundleCourseId = bundleLookupCourseId(items);
  const resolveItems = JSON.stringify(membershipResolveItems(items));

  // Each answer is kept with the question it answers, so a changed question
  // (signed out, another basket) reads as "nothing yet" at once instead of
  // offering the previous account's bundles while the new answer loads.
  const bundlesKey =
    accountEmail && bundleCourseId
      ? `${apiUrl}|${bundleCourseId}|${accountEmail}`
      : null;
  const membershipKey =
    accountEmail && resolveItems !== "[]"
      ? `${apiUrl}|${businessId}|${accountEmail}|${resolveItems}`
      : null;
  const [bundlesAnswer, setBundlesAnswer] = useState<{
    key: string;
    bundles: UserApplicableBundle[];
  } | null>(null);
  const [membershipAnswer, setMembershipAnswer] = useState<{
    key: string;
    resolution: MembershipResolution | null;
  } | null>(null);

  useEffect(() => {
    if (!bundlesKey || !accountEmail || !bundleCourseId) return;
    let cancelled = false;
    const settle = (bundles: UserApplicableBundle[]) => {
      if (!cancelled) setBundlesAnswer({ key: bundlesKey, bundles });
    };
    fetch(
      `${apiOrigin(apiUrl)}/widget/courses/${encodeURIComponent(bundleCourseId)}/user-applicable-bundles?email=${encodeURIComponent(accountEmail)}`,
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => settle(Array.isArray(data?.bundles) ? data.bundles : []))
      .catch(() => settle([]));
    return () => {
      cancelled = true;
    };
  }, [apiUrl, accountEmail, bundleCourseId, bundlesKey]);

  useEffect(() => {
    if (!membershipKey || !accountEmail) return;
    let cancelled = false;
    const settle = (resolution: MembershipResolution | null) => {
      if (!cancelled) setMembershipAnswer({ key: membershipKey, resolution });
    };
    const params = new URLSearchParams({
      email: accountEmail,
      schoolId: businessId,
      items: resolveItems,
    });
    fetch(`${apiOrigin(apiUrl)}/widget/app/membership/resolve?${params}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => settle(body?.data ?? null))
      .catch(() => settle(null));
    return () => {
      cancelled = true;
    };
  }, [apiUrl, businessId, accountEmail, resolveItems, membershipKey]);

  const userBundles =
    bundlesKey && bundlesAnswer?.key === bundlesKey ? bundlesAnswer.bundles : NO_BUNDLES;
  const membershipResolution =
    membershipKey && membershipAnswer?.key === membershipKey
      ? membershipAnswer.resolution
      : null;

  const eligibleBundles = useMemo(
    () => eligibleBundlesFor(items, userBundles),
    [items, userBundles],
  );

  return { eligibleBundles, membershipResolution };
}
